// SPDX-License-Identifier: AGPL-3.0-or-later

import {StatusSlate} from '@app/features/app/components/dialogs/shared/StatusSlate';
import type {CompanionSessionSummary} from '@app/features/companion/commands/CompanionCommands';
import styles from '@app/features/companion/components/records/CompanionRecords.module.css';
import {
	getCompanionAudioSourceDescriptor,
	getCompanionSessionDayGroups,
	getCompanionSessionDurationSeconds,
	getCompanionSessionLocalDateKey,
	getCompanionSessionModeDescriptor,
	getCompanionSessionStatusDescriptor,
} from '@app/features/companion/components/records/CompanionRecordsUtils';
import {CompanionSessionAudioChip} from '@app/features/companion/components/records/CompanionSessionAudioChip';
import CompanionSessionStore from '@app/features/companion/state/CompanionSessionStore';
import {Button} from '@app/features/ui/button/Button';
import {Combobox} from '@app/features/ui/components/form/FormCombobox';
import {Input} from '@app/features/ui/components/form/FormInput';
import {getRelativeDayLabelCapitalized} from '@app/features/ui/utils/RelativeDayLabels';
import * as DateUtils from '@app/features/user/utils/DateFormatting';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {ClockIcon, MagnifyingGlassIcon, SparkleIcon} from '@phosphor-icons/react';
import type React from 'react';
import {useCallback, useMemo, useState} from 'react';

const DEFAULT_SCENE_DESCRIPTOR = msg({
	message: 'Default scene',
	comment: 'Companion history scene label when the session used the default scene.',
});
const OPEN_SESSION_DESCRIPTOR = msg({
	message: 'Open companion session from {time}',
	comment: 'Accessible button label for opening a companion history session. Preserve the time placeholder.',
});
const UNDATED_GROUP_DESCRIPTOR = msg({
	message: 'Undated',
	comment: 'Companion history group header for sessions without a start date.',
});
const CREATED_AT_DESCRIPTOR = msg({
	message: 'Created {time}',
	comment: 'Companion history session card creation timestamp. Preserve {time}.',
});

function formatDayGroupLabel(key: string, locale: string): string {
	const now = new Date();
	if (key === getCompanionSessionLocalDateKey(now)) return getRelativeDayLabelCapitalized(locale, 0);
	const yesterday = new Date(now);
	yesterday.setDate(yesterday.getDate() - 1);
	if (key === getCompanionSessionLocalDateKey(yesterday)) return getRelativeDayLabelCapitalized(locale, -1);
	return new Intl.DateTimeFormat(locale, {month: 'numeric', day: 'numeric', weekday: 'short'}).format(
		new Date(`${key}T00:00:00`),
	);
}

interface CompanionSessionsListProps {
	sessions: ReadonlyArray<CompanionSessionSummary>;
	onSelectSession: (sessionId: string) => void;
	sceneNames?: Readonly<Record<string, string>>;
}

export const CompanionSessionsList: React.FC<CompanionSessionsListProps> = ({
	sessions,
	onSelectSession,
	sceneNames = {},
}) => {
	const {i18n} = useLingui();
	const [query, setQuery] = useState('');
	const [filter, setFilter] = useState('all');
	const visibleSessions = useMemo(
		() =>
			[...sessions]
				.sort((a, b) => (Date.parse(b.started_at ?? '') || 0) - (Date.parse(a.started_at ?? '') || 0))
				.filter((session) => {
					const status = session.status;
					const active =
						status === 'pending' ||
						status === 'starting' ||
						status === 'listening' ||
						status === 'paused' ||
						status === 'ending';
					const matchesStatus =
						filter === 'all' ||
						(filter === 'active'
							? active
							: filter === 'ended'
								? status === 'ended'
								: status === 'failed' || status === 'interrupted');
					const text = [
						session.scene_name,
						sceneNames[session.scene_id ?? ''],
						session.scene_id,
						session.summary,
						session.started_at ? DateUtils.getFormattedCompactDateTime(session.started_at) : session.session_id,
					]
						.join(' ')
						.toLocaleLowerCase(i18n.locale);
					return matchesStatus && text.includes(query.trim().toLocaleLowerCase(i18n.locale));
				}),
		[sessions, sceneNames, query, filter, i18n.locale],
	);
	const dayGroups = useMemo(() => getCompanionSessionDayGroups(visibleSessions), [visibleSessions]);
	const handleResetFilters = useCallback(() => {
		setQuery('');
		setFilter('all');
	}, []);
	if (sessions.length === 0) {
		return (
			<StatusSlate
				Icon={SparkleIcon}
				title={<Trans>No companion sessions yet</Trans>}
				description={<Trans>Live transcript and suggestions</Trans>}
				actions={[{text: i18n._(msg({message: 'Open AI companion'})), onClick: CompanionSessionStore.openPanel}]}
				data-flx="user.companion-records-tab.sessions-list.empty"
			/>
		);
	}
	return (
		<div className={styles.historyContainer} data-flx="companion.records.companion-sessions-list.history-container">
			<div className={styles.listToolbar} data-flx="companion.records.companion-sessions-list.list-toolbar">
				<div className={styles.searchField} data-flx="companion.records.companion-sessions-list.search-field">
					<Input
						type="search"
						aria-label={i18n._(msg({message: 'Search companion records'}))}
						placeholder={i18n._(msg({message: 'Search by scene or date'}))}
						value={query}
						onChange={(event) => setQuery(event.target.value)}
						leftIcon={
							<MagnifyingGlassIcon
								aria-hidden
								data-flx="companion.records.companion-sessions-list.magnifying-glass-icon"
							/>
						}
						data-flx="companion.records.companion-sessions-list.input.set-query.search"
					/>
				</div>
				<Combobox
					className={styles.filterControl}
					aria-label={i18n._(msg({message: 'Filter by status'}))}
					value={filter}
					options={[
						{value: 'all', label: i18n._(msg({message: 'All sessions'}))},
						{value: 'active', label: i18n._(msg({message: 'In progress'}))},
						{value: 'ended', label: i18n._(msg({message: 'Ended'}))},
						{value: 'failed', label: i18n._(msg({message: 'Failed or interrupted'}))},
					]}
					onChange={setFilter}
					isSearchable={false}
					data-flx="user.companion-records-tab.sessions-list.filter"
				/>
			</div>
			<div
				className={styles.listCaption}
				role="status"
				data-flx="companion.records.companion-sessions-list.list-caption"
			>
				{i18n._(msg({message: 'Showing {visible} of {total} sessions'}), {
					visible: visibleSessions.length,
					total: sessions.length,
				})}
			</div>
			{dayGroups.map((group) => (
				<section
					key={group.key}
					className={styles.dateGroup}
					data-flx="user.companion-records-tab.sessions-list.date-group"
				>
					<h3 className={styles.dateGroupHeader} data-flx="user.companion-records-tab.sessions-list.date-group-header">
						{group.key === 'undated' ? i18n._(UNDATED_GROUP_DESCRIPTOR) : formatDayGroupLabel(group.key, i18n.locale)}
					</h3>
					<ul className={styles.sessionList} data-flx="user.companion-records-tab.sessions-list.list">
						{group.sessions.map((session) => {
							const time = session.started_at ? DateUtils.getFormattedTime(session.started_at) : session.session_id;
							const scene =
								session.scene_name ||
								sceneNames[session.scene_id ?? ''] ||
								session.scene_id ||
								i18n._(DEFAULT_SCENE_DESCRIPTOR);
							const modeDescriptor = getCompanionSessionModeDescriptor(session.mode);
							const summaryParts = [
								modeDescriptor ? i18n._(modeDescriptor) : null,
								i18n._(getCompanionAudioSourceDescriptor(session.audio_source)),
							].filter((part): part is string => part !== null);
							return (
								<li
									key={session.session_id}
									className={styles.sessionCard}
									data-flx="user.companion-records-tab.sessions-list.item"
								>
									<button
										type="button"
										className={styles.sessionButton}
										onClick={() => onSelectSession(session.session_id)}
										aria-label={i18n._(OPEN_SESSION_DESCRIPTOR, {time})}
										data-flx="user.companion-records-tab.sessions-list.button.open"
									>
										<span
											className={styles.sessionTitleRow}
											data-flx="user.companion-records-tab.sessions-list.title-row"
										>
											<span className={styles.sessionTime} data-flx="user.companion-records-tab.sessions-list.time">
												{scene}
											</span>
											<span
												className={styles.statusBadge}
												data-status={session.status}
												data-flx="user.companion-records-tab.sessions-list.status"
											>
												{i18n._(getCompanionSessionStatusDescriptor(session.status))}
											</span>
										</span>
										<span className={styles.sessionSummary} data-flx="user.companion-records-tab.sessions-list.summary">
											{summaryParts.join(' · ')}
										</span>
										{session.summary?.trim() ? (
											<span
												className={styles.sessionSummaryPreview}
												data-flx="user.companion-records-tab.sessions-list.summary-preview"
											>
												{session.summary.trim()}
											</span>
										) : null}
									</button>
									<div className={styles.sessionExtras} data-flx="user.companion-records-tab.sessions-list.extras">
										<CompanionSessionAudioChip
											sessionId={session.session_id}
											durationSeconds={getCompanionSessionDurationSeconds(session)}
											data-flx="companion.records.companion-sessions-list.companion-session-audio-chip"
										/>
										<div className={styles.tagRow} data-flx="user.companion-records-tab.sessions-list.tag-row">
											<span
												className={styles.tagPill}
												data-variant="primary"
												data-flx="user.companion-records-tab.sessions-list.tag-pill--scene"
											>
												<SparkleIcon aria-hidden data-flx="companion.records.companion-sessions-list.sparkle-icon" />
												{scene}
											</span>
											{modeDescriptor ? (
												<span
													className={styles.tagPill}
													data-flx="user.companion-records-tab.sessions-list.tag-pill--mode"
												>
													{i18n._(modeDescriptor)}
												</span>
											) : null}
											<span
												className={styles.tagPill}
												data-flx="user.companion-records-tab.sessions-list.tag-pill--source"
											>
												{i18n._(getCompanionAudioSourceDescriptor(session.audio_source))}
											</span>
										</div>
										<div className={styles.sessionFooter} data-flx="user.companion-records-tab.sessions-list.footer">
											<ClockIcon aria-hidden data-flx="companion.records.companion-sessions-list.clock-icon" />
											{i18n._(CREATED_AT_DESCRIPTOR, {time})}
										</div>
									</div>
								</li>
							);
						})}
					</ul>
				</section>
			))}
			{visibleSessions.length === 0 ? (
				<div className={styles.searchEmpty} data-flx="companion.records.companion-sessions-list.search-empty">
					<MagnifyingGlassIcon
						aria-hidden
						data-flx="companion.records.companion-sessions-list.magnifying-glass-icon--2"
					/>
					<p data-flx="companion.records.companion-sessions-list.p">{i18n._(msg({message: 'No matching sessions'}))}</p>
					<Button
						variant="ghost"
						superCompact
						fitContent
						onClick={handleResetFilters}
						data-flx="user.companion-records-tab.sessions-list.button.reset-filters"
					>
						{i18n._(msg({message: 'Clear filters'}))}
					</Button>
				</div>
			) : null}
		</div>
	);
};
